import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Cookie, X } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

const STORAGE_KEY = "myswim_cookie_consent_v1";
// Re-ask after 13 months per CNIL recommendation
const EXPIRY_DAYS = 395;

const defaultPrefs = { essential: true, analytics: false, marketing: false };

function readStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.savedAt) return null;
    const ageDays = (Date.now() - parsed.savedAt) / (1000 * 60 * 60 * 24);
    if (ageDays > EXPIRY_DAYS) return null;
    return parsed.prefs;
  } catch { return null; }
}

function persist(prefs) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify({ prefs, savedAt: Date.now() }));
  window.dispatchEvent(new CustomEvent("cookies:updated", { detail: prefs }));
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    const stored = readStored();
    if (!stored) setOpen(true);
    else setPrefs(stored);
    const onReopen = () => { setShowSettings(true); setOpen(true); };
    window.addEventListener("cookies:reopen", onReopen);
    return () => window.removeEventListener("cookies:reopen", onReopen);
  }, []);

  const acceptAll = () => {
    const next = { essential: true, analytics: true, marketing: true };
    persist(next); setPrefs(next); setOpen(false); setShowSettings(false);
  };
  const rejectAll = () => {
    persist(defaultPrefs); setPrefs(defaultPrefs); setOpen(false); setShowSettings(false);
  };
  const saveCustom = () => {
    persist({ ...prefs, essential: true });
    setOpen(false); setShowSettings(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 bottom-0 z-[60] md:bottom-4 md:left-4 md:right-auto md:max-w-md pointer-events-none" data-testid="cookie-banner">
      <div className="m-3 md:m-0 rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden pointer-events-auto">
        {!showSettings ? (
          <div className="p-5">
            <div className="flex items-start gap-3 mb-3">
              <div className="bg-amber-100 text-amber-700 rounded-xl p-2 shrink-0"><Cookie className="w-5 h-5" /></div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Vos préférences cookies</h3>
                <p className="text-sm text-slate-600 mt-1">
                  Nous utilisons des cookies pour faire fonctionner le site, mesurer son audience et améliorer votre expérience. Vous pouvez accepter, refuser ou personnaliser vos choix à tout moment.{" "}
                  <Link to="/legal/privacy" className="text-sky-700 underline">En savoir plus</Link>.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              <Button onClick={acceptAll} className="rounded-full bg-sky-700 hover:bg-sky-600 flex-1 min-w-[120px]" data-testid="cookies-accept-all">
                Tout accepter
              </Button>
              <Button onClick={rejectAll} variant="outline" className="rounded-full flex-1 min-w-[120px]" data-testid="cookies-reject-all">
                Tout refuser
              </Button>
              <Button onClick={() => setShowSettings(true)} variant="ghost" className="rounded-full text-sky-700 w-full" data-testid="cookies-customize">
                Personnaliser
              </Button>
            </div>
          </div>
        ) : (
          <div className="p-5 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>Personnaliser vos cookies</h3>
              <button onClick={() => setShowSettings(false)} className="text-slate-400 hover:text-slate-700" aria-label="Fermer"><X className="w-4 h-4" /></button>
            </div>
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-3 border-b pb-3">
                <div>
                  <div className="font-semibold text-sm">Strictement nécessaires</div>
                  <div className="text-xs text-slate-500 mt-0.5">Authentification, sécurité, panier. Indispensables au fonctionnement.</div>
                </div>
                <Switch checked disabled data-testid="cookies-essential" />
              </div>
              <div className="flex items-start justify-between gap-3 border-b pb-3">
                <div>
                  <div className="font-semibold text-sm">Mesure d'audience</div>
                  <div className="text-xs text-slate-500 mt-0.5">Statistiques anonymisées pour comprendre l'usage du site.</div>
                </div>
                <Switch checked={prefs.analytics} onCheckedChange={(v) => setPrefs({ ...prefs, analytics: v })} data-testid="cookies-analytics" />
              </div>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-sm">Marketing</div>
                  <div className="text-xs text-slate-500 mt-0.5">Personnalisation et ciblage publicitaire.</div>
                </div>
                <Switch checked={prefs.marketing} onCheckedChange={(v) => setPrefs({ ...prefs, marketing: v })} data-testid="cookies-marketing" />
              </div>
            </div>
            <div className="flex flex-wrap gap-2 mt-5">
              <Button onClick={saveCustom} className="rounded-full bg-sky-700 hover:bg-sky-600 flex-1" data-testid="cookies-save">
                Enregistrer mes choix
              </Button>
              <Button onClick={acceptAll} variant="outline" className="rounded-full flex-1" data-testid="cookies-accept-all-2">
                Tout accepter
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
